import { useState, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { commands } from '@/lib/platform/generated/bindings.gen';
import { clsx } from 'clsx';
import { Alert, Button, Flex, Text, Select } from '@nula/core-ui';
import styles from './device-selector.module.css';

// AudioDeviceInfo from bindings has the same shape { id, name }
// The local alias is kept for clarity within this module.
type AudioDevice = { id: string; name: string };

export type DeviceSelectorProps = {
  micDeviceId: string | null;
  systemDeviceId: string | null;
  onChange: (micId: string | null, sysId: string | null) => void;
  /**
   * Whether macOS Microphone permission is granted. Defaults to `true` so no
   * warning appears on non-macOS or before the live probe resolves.
   */
  micGranted?: boolean;
  /**
   * Whether macOS Screen Recording permission is granted (needed for system
   * audio). Defaults to `true`.
   */
  screenGranted?: boolean;
  /** Opens System Settings to the relevant Privacy pane. */
  onOpenSystemSettings?: (pane: 'microphone' | 'screen_recording') => void;
  /**
   * Richer recovery: when set, each warning shows Request / Reset / Open inline
   * actions (matching the permission-row pattern) instead of just an Open
   * button. `action` is one of 'request' | 'reset' | 'open'.
   */
  onPermissionAction?: (
    pane: 'microphone' | 'screen_recording',
    action: 'request' | 'reset' | 'open',
  ) => void;
  className?: string;
};

const root = cva(styles.root);
const deviceRow = cva(styles.deviceRow);

const SYSTEM_DEFAULT_OPTION = [{ value: 'default', label: 'System default' }];

function devicesToOptions(devices: AudioDevice[]) {
  if (!devices.length) return SYSTEM_DEFAULT_OPTION;
  return [
    { value: 'default', label: 'System default' },
    ...devices.map((d) => ({ value: d.id, label: d.name })),
  ];
}

function idToValue(id: string | null): string {
  return id ?? 'default';
}

function valueToId(v: string): string | null {
  return v === 'default' ? null : v;
}

export function DeviceSelector({
  micDeviceId,
  systemDeviceId,
  onChange,
  micGranted = true,
  screenGranted = true,
  onOpenSystemSettings,
  onPermissionAction,
  className,
}: DeviceSelectorProps) {
  const [options, setOptions] = useState(SYSTEM_DEFAULT_OPTION);

  useEffect(() => {
    // commands.listAudioDevices() returns AudioDeviceInfo[] directly (no Result wrapper).
    // Falls back to "System default" on any error (IPC failure, unsupported platform).
    commands.listAudioDevices()
      .then((devices) => setOptions(devicesToOptions(devices as AudioDevice[])))
      .catch(() => setOptions(SYSTEM_DEFAULT_OPTION));
  }, []);

  // Per-warning recovery actions. Prefer the richer Request/Reset/Open trio
  // when onPermissionAction is provided; otherwise fall back to the single
  // Open button (back-compat for callers that don't wire the trio).
  const renderActions = (pane: 'microphone' | 'screen_recording') => {
    if (onPermissionAction) {
      return (
        <Flex gap="sm">
          <Button size="sm" variant="default" onClick={() => onPermissionAction(pane, 'request')}>
            Request
          </Button>
          <Button size="sm" variant="default" onClick={() => onPermissionAction(pane, 'reset')}>
            Reset
          </Button>
          <Button size="sm" variant="default" onClick={() => onPermissionAction(pane, 'open')}>
            Open
          </Button>
        </Flex>
      );
    }
    if (onOpenSystemSettings) {
      return (
        <Button size="sm" variant="default" onClick={() => onOpenSystemSettings(pane)}>
          Open System Settings
        </Button>
      );
    }
    return undefined;
  };

  return (
    <Flex vertical className={clsx(root(), className)}>
      <Flex vertical className={deviceRow()}>
        <Text size="sm" className={styles.label}>Microphone</Text>
        <Select
          options={options}
          size="sm"
          value={idToValue(micDeviceId)}
          onChange={(v) => onChange(valueToId(v as string), systemDeviceId)}
        />
        {!micGranted && (
          <Alert
            type="warning"
            showIcon
            message="Microphone access is off"
            description="Recordings will be silent until you grant Microphone access to Nula in System Settings."
            action={renderActions('microphone')}
          />
        )}
      </Flex>
      <Flex vertical className={deviceRow()}>
        <Text size="sm" className={styles.label}>System audio</Text>
        <Select
          options={options}
          size="sm"
          value={idToValue(systemDeviceId)}
          onChange={(v) => onChange(micDeviceId, valueToId(v as string))}
        />
        {!screenGranted && (
          <Alert
            type="warning"
            showIcon
            message="Screen Recording access is off"
            description="System audio won't be captured until you grant Screen Recording access to Nula in System Settings."
            action={renderActions('screen_recording')}
          />
        )}
      </Flex>
    </Flex>
  );
}
